import React from 'react';
import { education, experience, certificates } from '../data/mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { GraduationCap, Briefcase, Award, ExternalLink, UserCircle } from 'lucide-react';

const Education = () => {
  // Logo URLs for companies/institutions
  const telkomLogo = "https://customer-assets.emergentagent.com/job_muhajir-dev/artifacts/640onyy5_1000143115.png";
  const bangkitLogo = "https://media.licdn.com/dms/image/v2/D560BAQGVomgVddrtBA/company-logo_200_200/B56ZWOrFbWGUAM-/0/1741855415072/bangkit_academy_logo?e=2147483647&v=beta&t=oWNWz9O6b8rrBzaHIYm0P8JDa0hYPcNOJcPJMa_jpcY";
  const infiniteLogo = "https://customer-assets.emergentagent.com/job_muhajir-dev/artifacts/ql811dyl_1000143117.png";

  const getExperienceIcon = (company) => {
    if (company.includes('Bangkit')) return bangkitLogo;
    if (company.includes('Infinite Learning')) return infiniteLogo;
    return null; // Will use lucide icon for others
  };

  return (
    <section id="education" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 text-center">
          Education & Experience
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          My educational background, experience, and certifications
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Education */}
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
              <GraduationCap className="h-6 w-6 mr-2 text-primary" />
              Education
            </h3>
            <div className="space-y-4">
              {education.map((edu) => (
                <Card key={edu.id} className="border-border hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-white flex items-center justify-center p-1">
                        <img 
                          src={telkomLogo} 
                          alt="Telkom University"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{edu.degree}</CardTitle>
                        <CardDescription className="flex flex-col gap-1">
                          <span className="font-medium text-foreground">{edu.institution}</span>
                          <span>{edu.period}</span>
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{edu.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
              <Briefcase className="h-6 w-6 mr-2 text-primary" />
              Experience
            </h3>
            <div className="space-y-4">
              {experience.map((exp) => {
                const logoUrl = getExperienceIcon(exp.company);
                return (
                  <Card key={exp.id} className="border-border hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-white flex items-center justify-center p-1">
                          {logoUrl ? (
                            <img 
                              src={logoUrl} 
                              alt={exp.company}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <UserCircle className="h-8 w-8 text-primary" />
                          )}
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg">{exp.title}</CardTitle>
                          <CardDescription className="flex flex-col gap-1">
                            <span className="font-medium text-foreground">{exp.company}</span>
                            <span>{exp.period}</span>
                            {exp.location && <span className="text-xs">{exp.location}</span>}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">{exp.description}</p>
                      {exp.skills && (
                        <p className="text-xs text-muted-foreground italic">
                          <span className="font-semibold">Skills:</span> {exp.skills}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* Certificates */}
        <div>
          <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center justify-center">
            <Award className="h-6 w-6 mr-2 text-primary" />
            Certifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map((cert) => (
              <Card key={cert.id} className="border-border hover:shadow-lg transition-all hover:scale-105">
                <CardHeader>
                  <CardTitle className="text-base">{cert.title}</CardTitle>
                  <CardDescription>{cert.issuer}</CardDescription>
                </CardHeader>
                <CardContent>
                  <a
                    href={cert.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    View Certificate
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;
